import { Driver, User } from "@fairfleet/geotab";
import { t } from "i18next";
import { Key } from "../models/Key";
import { LicenseCheckData } from "../models/LicenseCheckData";
import { LicenseCheckDisplayData } from "../models/LicenseCheckDisplayData";
import { NextCheckData } from "../models/NextCheckData";
import calcDayDiff from "./calcDayDiff";

export default function licenseCheckRowsByUser(
  user: User,
  id: number,
  licenseChecks: LicenseCheckData[],
  nextCheckDates: NextCheckData[],
): LicenseCheckDisplayData[] {
  const userLicenseChecks = licenseChecks.filter(check => check.userid === user.id);
  const userType = user.isDriver ? "Fahrer" : "Benutzer";
  const depositedKey =
    ((user as Driver).keys as Key[]).filter(key => key.driverKeyType === "Custom")[0]
      ?.serialNumber ?? "---";
  const isChecked = !!userLicenseChecks.length;
  if (!isChecked)
    return [
      {
        id: "" + id,
        firstName: user.firstName,
        lastName: user.lastName,
        name: user.name,
        userId: user.id,
        userType,
        depositedKey,
        usedKey: "---",
        statusMessage: "---",
        lastVerification: "---",
        daysSinceLastPositiveVerification: "---",
        nextCheckDate: new Date(),
        timeOverdue: "---",
        lastPositiveCheckDate: "---",
        originalCheckDate: "---",
        nextCheckEntityId: "---",
        verificationDateTime: "---",
      },
    ];
  const messages = ["Verifiziert", "Fehlgeschlagen", "Abgebrochen"];

  const latestVerification = userLicenseChecks.sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
  )[0];

  const lastVerification = latestVerification.timestamp;

  const licenseCheckRows = userLicenseChecks.map((userLicenseCheck, lid) => {
    const usedKey = userLicenseCheck.key;
    const statusMessage =
      userLicenseCheck.statusCode === 1
        ? messages[0]
        : userLicenseCheck.statusCode === 5
          ? messages[1]
          : messages[2];

    const lastPositiveCheckDate = lastPositiveCheck(userLicenseChecks) ?? "---";

    const originalCheckDate =
      userLicenseCheck.statusCode === 1
        ? "---"
        : (overDueSinceDate(userLicenseCheck, userLicenseChecks) ?? "---");

    const daysSinceLastPositiveVerification = calcDayDiff(lastPositiveCheckDate);

    const nextCheckDate = nextCheckDates?.find(check => check.userid === user.id);
    const nextCheckEntityId = nextCheckDate?.id ?? "";

    const timeOverdue =
      originalCheckDate != "---"
        ? Math.floor((new Date().getTime() - originalCheckDate.getTime()) / 1000)
        : "---";

    return {
      id: id + " " + lid,
      firstName: user.firstName,
      lastName: user.lastName,
      name: user.name,
      userId: user.id,
      userType,
      depositedKey,
      usedKey,
      statusMessage: t("licenseCheckState." + statusMessage),
      lastVerification,
      daysSinceLastPositiveVerification,
      nextCheckDate: nextCheckDate?.nextCheckDate ?? new Date(),
      timeOverdue,
      lastPositiveCheckDate,
      originalCheckDate,
      nextCheckEntityId,
      verificationDateTime: new Date(userLicenseCheck.timestamp),
    };
  });
  return licenseCheckRows;
}

function lastPositiveCheck(pLicenseChecks: LicenseCheckData[]): Date | undefined {
  const checks = pLicenseChecks.filter(check => check.statusCode === 1);
  return checks.length === 0
    ? undefined
    : checks.reduce((prev, current) =>
        current.timestamp.getTime() > prev.timestamp.getTime() ? current : prev,
      ).timestamp;
}

function overDueSinceDate(
  currentCheck: LicenseCheckData,
  pLicenseChecks: LicenseCheckData[],
): Date | undefined {
  pLicenseChecks = pLicenseChecks.filter(c => c.timestamp <= currentCheck.timestamp);
  const sortedChecks = pLicenseChecks.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  for (const check of sortedChecks) {
    return check?.originalCheckDate;
  }
}
